/**
 * Canonical post-auth redirect policy. Single source of truth for where to send
 * the user after login/register. Pure and testable; no side effects.
 */

import type { AuthIntent } from "./auth-intent";
import { buildJoinUrlFromIntent } from "./auth-intent";
import { getSafeRedirectRedirect, toSafeAppRedirect } from "./safe-redirect";

export type ContextMode = "personal" | "organization" | "platform" | null;

export type ResolvePostAuthTargetArgs = {
  authIntent: AuthIntent | null;
  currentPath: string;
  searchParams: URLSearchParams;
  contextMode: ContextMode;
  storedReturnUrl?: string | null;
};

function normalizeTargetForContext(
  target: string,
  contextMode: ContextMode,
): string {
  if (
    contextMode === "platform" &&
    (target === "/app" || (target.startsWith("/app/") && !target.startsWith("/app/sprava")))
  ) {
    return "/app/sprava";
  }

  return target;
}

/**
 * Returns the path to navigate to after auth, or null if no navigation needed
 * (e.g. already on target). A safe explicit destination from the current link
 * wins over a return URL or invite left in this tab by an older journey.
 */
export function resolvePostAuthTarget(args: ResolvePostAuthTargetArgs): string | null {
  const { authIntent, currentPath, searchParams, contextMode, storedReturnUrl } = args;
  const normalizedCurrent = currentPath.trim() || "/";

  // JOIN_ORG registration consumes the invite server-side in the registration
  // transaction. Once auth context is already organization while still on the
  // register route, routing back to /join would re-process an already-used
  // invite and surface a false error after a successful registration.
  const isRegisterRoute =
    normalizedCurrent === "/register" || normalizedCurrent.startsWith("/register?");
  const redirectFromUrl = getSafeRedirectRedirect(searchParams);
  const currentUrl = new URL(normalizedCurrent, "https://return.invalid");
  if (!redirectFromUrl && currentUrl.pathname === "/join" &&
      (currentUrl.searchParams.get("token") || currentUrl.searchParams.get("code"))) {
    // A newly opened school invitation is already the destination. An old
    // activity continuation must not replace it before its own checks run.
    return null;
  }
  const storedTarget = storedReturnUrl ? toSafeAppRedirect(storedReturnUrl) : null;
  const currentTarget = redirectFromUrl ?? storedTarget;
  const joinRegistrationAlreadyCompleted =
    isRegisterRoute &&
    contextMode === "organization" &&
    (currentTarget
      ? new URL(currentTarget, "https://return.invalid").pathname === "/join"
      : authIntent?.type === "JOIN");
  if (joinRegistrationAlreadyCompleted) {
    return "/app";
  }

  // 1) The current link, then an explicitly saved continuation (e.g. password change).
  if (currentTarget) {
    const target = normalizeTargetForContext(currentTarget, contextMode);
    return normalizedCurrent === target ? null : target;
  }

  // 2) A school invitation remains valid when there is no newer destination.
  if (authIntent) {
    if (authIntent.type === "JOIN") {
      const target = normalizeTargetForContext(
        buildJoinUrlFromIntent(authIntent),
        contextMode,
      );
      return normalizedCurrent === target ? null : target;
    }
    if (authIntent.type === "RETURN_TO") {
      const safePath = toSafeAppRedirect(authIntent.path);
      if (!safePath) return null;
      const target = normalizeTargetForContext(safePath, contextMode);
      return normalizedCurrent === target ? null : target;
    }
  }

  // 3) Context-based fallback
  let fallback: string;
  if (contextMode === "personal") {
    fallback = "/onboarding/create-organization";
  } else if (contextMode === "platform") {
    fallback = "/app/sprava";
  } else if (contextMode === "organization") {
    fallback = "/app";
  } else {
    fallback = "/app";
  }
  return normalizedCurrent === fallback ? null : fallback;
}

/** Entry destinations that require a school account, not school creation. */
export function isStudentActivityPath(path: string): boolean {
  const safe = toSafeAppRedirect(path);
  if (!safe) return false;
  const pathname = new URL(safe, "https://return.invalid").pathname.replace(/\/$/, "");
  return pathname === "/app/pripojit" ||
    /^\/app\/zive-hry\/[^/]+\/hra$/.test(pathname) ||
    /^\/app\/zadani\/[^/]+(?:\/test)?$/.test(pathname) ||
    pathname === "/app/laborator/algoritmy" ||
    pathname === "/app/laborator/algoritmy/pripojit";
}
